import { useMemo, useState } from 'react';
import type { FileScoutFindings } from '@sr-test-wizard/shared';

interface Props {
  findings: FileScoutFindings[];
}

/**
 * Per-file source-code "scout" findings, surfaced inline under the AI
 * assessment card. Tier-1 scout reads every file end-to-end and extracts
 * the line ranges the flagship aggregator (tier 2) reasoned from. Showing
 * them in the UI gives the tester a way to:
 *
 *   - confirm the scout actually saw the configuration the verdict depends
 *     on (e.g. for a max-win test, confirm the maxWin line is in excerpts),
 *   - spot scout failures (model 400, context overflow, bad JSON parse)
 *     that the aggregator would otherwise mask,
 *   - audit which files contributed evidence vs were skipped.
 *
 * The panel collapses to a single line by default to keep the dense Phase 6
 * view readable; click a row to expand its summary, excerpts, and open
 * questions. Excerpts are rendered with `<pre>` so verbatim source layout
 * survives. We never render scout output as Markdown - it's source code,
 * not prose.
 */
export function ScoutFindingsPanel({ findings }: Props) {
  const [openAll, setOpenAll] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const counts = useMemo(() => {
    const total = findings.length;
    const relevant = findings.filter((f) => f.is_relevant && !f.scout_error).length;
    const errors = findings.filter((f) => !!f.scout_error).length;
    return { total, relevant, errors };
  }, [findings]);

  if (!findings || findings.length === 0) return null;

  const toggleRow = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAll = () => {
    if (openAll) {
      setExpanded({});
      setOpenAll(false);
    } else {
      const next: Record<string, boolean> = {};
      for (const f of findings) next[f.sha1 || f.relative_path || f.file] = true;
      setExpanded(next);
      setOpenAll(true);
    }
  };

  return (
    <div className="ai-card" style={{ marginTop: 8 }}>
      <div className="row" style={{ alignItems: 'center' }}>
        <strong>Per-file scout findings</strong>
        <span className="badge small">{counts.relevant} relevant</span>
        <span className="badge small">{counts.total} scanned</span>
        {counts.errors > 0 && (
          <span className="badge bug small" title="Files where the scout call itself failed">
            {counts.errors} scout error{counts.errors === 1 ? '' : 's'}
          </span>
        )}
        <span className="spacer" />
        <button
          type="button"
          className="btn btn-ghost small"
          onClick={expandAll}
          title={openAll ? 'Collapse all files' : 'Expand all files'}
        >
          {openAll ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
      <p className="muted small" style={{ margin: '4px 0 8px' }}>
        Each file below was read end-to-end by the source-code scout model; the
        verdict above was issued by the flagship model from the cited line
        ranges and verbatim excerpts.
      </p>
      <div className="flex-col" style={{ gap: 4 }}>
        {findings.map((f) => {
          const key = f.sha1 || f.relative_path || f.file;
          const isOpen = !!expanded[key];
          return (
            <div
              key={key}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 6,
                background: 'var(--bg-elev-1)',
              }}
            >
              <button
                type="button"
                onClick={() => toggleRow(key)}
                className="row"
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  alignItems: 'center',
                  gap: 8,
                }}
                title={f.relative_path}
              >
                <span aria-hidden="true" style={{ width: 14, textAlign: 'center' }}>
                  {isOpen ? '\u25BC' : '\u25B6'}
                </span>
                {f.scout_error ? (
                  <span className="badge bug small">scout error</span>
                ) : f.is_relevant ? (
                  <span className="badge ok small">relevant</span>
                ) : (
                  <span className="badge small">skipped</span>
                )}
                <code className="mono small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: '0 1 auto' }}>
                  {f.file}
                </code>
                <span
                  className="muted small"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: '1 1 auto',
                    minWidth: 0,
                  }}
                >
                  {f.summary || '(no summary)'}
                </span>
                <span className="muted small" style={{ flexShrink: 0 }}>
                  {f.excerpts.length} excerpt{f.excerpts.length === 1 ? '' : 's'}
                </span>
              </button>
              {isOpen && (
                <div style={{ padding: '0 12px 10px' }}>
                  <div className="muted small" style={{ marginBottom: 6 }}>
                    {f.relative_path}
                    {f.sha1 ? <> {'\u00B7'} sha1 <code className="mono">{f.sha1.slice(0, 10)}</code></> : null}
                  </div>
                  {f.scout_error && (
                    <div
                      role="alert"
                      style={{
                        margin: '0 0 8px',
                        padding: '6px 10px',
                        border: '1px solid var(--bad)',
                        background: 'rgba(248, 81, 73, 0.08)',
                        borderRadius: 6,
                        color: 'var(--bad)',
                        fontSize: 12,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      <strong>Scout error:</strong> {f.scout_error}
                    </div>
                  )}
                  {f.excerpts.length === 0 && !f.scout_error && (
                    <p className="muted small" style={{ margin: 0 }}>
                      No excerpts cited - the scout judged this file irrelevant for the test.
                    </p>
                  )}
                  {f.excerpts.map((ex, idx) => {
                    const [start, end] = ex.lines;
                    const rangeLabel = start === end ? `Line ${start}` : `Lines ${start}\u2013${end}`;
                    return (
                      <div key={idx} className="evidence-code-block" style={{ marginBottom: 8 }}>
                        <div className="row" style={{ alignItems: 'baseline', gap: 6 }}>
                          <code className="mono small">{rangeLabel}</code>
                          <span className="muted small" style={{ flex: 1 }}>
                            {ex.reasoning}
                          </span>
                        </div>
                        <pre style={{ maxHeight: 240, overflow: 'auto' }}>{ex.content}</pre>
                      </div>
                    );
                  })}
                  {f.open_questions.length > 0 && (
                    <>
                      <strong className="small">Open questions for the aggregator:</strong>
                      <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                        {f.open_questions.map((q, i) => (
                          <li key={i} className="small">
                            {q}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
